import ArrowIcon from 'assets/svg/arrow/arrow.svg'
import Dropdown from 'common/components/Dropdown'
import Flex from 'common/components/Flex'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import usePagination from 'lib/hooks/UsePagination'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

const widthPaginationItem = '34px'
const heightPaginationItem = '40px'
const paddingPaginationItem = '10px 7px'

const Container = styled.div`
  display: flex;
  align-items: center;
  height: ${heightPaginationItem};
  justify-content: space-between;
  width: inherit;
  padding: ${(props) => props?.padding};
`

const setStyleDisabledIcon = (isDisabled, color) => {
  if (isDisabled) {
    return `
      pointer-events: none;
      svg {
        path {
          fill: ${theme.colors.black20};
        }
      }
    `
  }
  return `
    cursor: pointer;
    svg {
      path {
        fill: ${color};
      }
    }
  `
}

const PreviousPageBtn = styled.button`
  background: transparent;
  border: none;
  height: ${heightPaginationItem};
  padding: ${paddingPaginationItem};
  margin-right: 10px;
  svg {
    transform: rotate(-90deg);
  }
  ${(props) => setStyleDisabledIcon(props.isDisabled, props.color)}
`

const NextPageBtn = styled.button`
  background: transparent;
  border: none;
  height: ${heightPaginationItem};
  padding: ${paddingPaginationItem};
  margin-left: 10px;
  svg {
    transform: rotate(90deg);
  }
  ${(props) => setStyleDisabledIcon(props.isDisabled, props.color)}
`

const setStyleActiveItem = (isActive, color) => {
  if (isActive) {
    return `
      background-color: ${color};
      pointer-events: none;
    `
  }
  return `
    background-color: transparent;
    cursor: pointer;
  `
}

const Item = styled.button`
  border: none;
  border-radius: ${theme.radius.base};
  width: ${widthPaginationItem};
  height: ${heightPaginationItem};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${paddingPaginationItem};
  ${(props) => setStyleActiveItem(props.isActive, props.color)}
`

const Ellipsis = styled.button`
  background-color: transparent;
  border: none;
  width: ${widthPaginationItem};
  height: ${heightPaginationItem};
  padding: ${paddingPaginationItem};
  font-weight: bold;
`

const HiddenPaginationLink = styled.div`
  display: none;
`

const JumperInput = styled.input.attrs({ type: 'text' })`
  width: 70px;
  height: 40px;
  border-radius: inherit;
  font-family: 'Noto Sans', sans-serif;
  border: none;
  outline: none;
  box-sizing: border-box;
  padding: 8px 15px;
  display: flex;
  border-radius: ${theme.radius.lg};
  text-align: center;
  border: 2px solid ${theme.colors.grayLightAlt};
  ${theme.typography.bsubtitle};
`

interface IHiddenPagination {
  currentPage: number
  paginationRange: any[]
  lastPage: number
  renderHiddenPaginationLink?: boolean
  hiddenPaginationLinkPath?: string
}

export interface IPagination
  extends Omit<
    IHiddenPagination,
    'currentPage' | 'paginationRange' | 'lastPage'
  > {
  color?: string
  limit?: number
  totalData: number
  initialPage?: number
  siblingCount?: number
  onChange?: (page: number) => void
  showDropdown?: boolean
  dropdownValues?: number[]
  onChangeDropdown?: (value: number) => void
  showJumper?: boolean
  padding?: string
}

const DEFAULT_DROPDOWN_VALUES = [10, 20, 50]
const ENTER_CODE = 13
const numberRegex = /^(?!-)\d+$/

function HiddenPagination({
  currentPage,
  lastPage,
  paginationRange,
  hiddenPaginationLinkPath,
  renderHiddenPaginationLink,
}: IHiddenPagination) {
  if (!renderHiddenPaginationLink || !hiddenPaginationLinkPath)
    return null

  return (
    <HiddenPaginationLink data-testid="hidden-pagination-link-test">
      {currentPage > 1 &&
        !paginationRange.includes(currentPage - 1) && (
          <a
            href={`${hiddenPaginationLinkPath}?page=${
              currentPage - 1
            }`}
          >
            {currentPage - 1}
          </a>
        )}

      {paginationRange.map((item) => {
        if (item === 'DOTS') return null

        return (
          <a
            href={`${hiddenPaginationLinkPath}?page=${item}`}
            key={item}
            data-testid={`hidden-pagination-item-${item}-test`}
          >
            {item}
          </a>
        )
      })}

      {currentPage < lastPage &&
        !paginationRange.includes(currentPage + 1) && (
          <a
            href={`${hiddenPaginationLinkPath}?page=${
              currentPage + 1
            }`}
          >
            {currentPage + 1}
          </a>
        )}
    </HiddenPaginationLink>
  )
}

/**
 * @param padding
 * @param color The active color
 * @param limit limit number of data in a page
 * @param totalData Total number of data in all pages
 * @param initialPage The initial page selected
 * @param onChange Called when the currentPage is changed
 * @param renderHiddenPaginationLink For Search Engine
 * @param hiddenPaginationLinkPath For Search Engine
 * @param showDropdown Show dropdown to change data limit
 * @param dropdownValues The values of dropdown
 * @param onChangeDropdown Called when the dropdown value is changed
 * @param showJumper Show jumper to direct change page number
 * @constructor
 */
function Pagination({
  padding = '0 10px',
  color = theme.colors.orange,
  limit,
  totalData,
  initialPage = 1,
  siblingCount = 3,
  onChange = () => null,
  renderHiddenPaginationLink = false,
  hiddenPaginationLinkPath = '',
  showDropdown = false,
  dropdownValues = DEFAULT_DROPDOWN_VALUES,
  onChangeDropdown = () => null,
  showJumper = false,
}: IPagination): JSX.Element {
  // Local States
  const [dataLimit, setDataLimit] = useState(
    limit ?? dropdownValues[0],
  )
  const [currentPage, setCurrentPage] = useState(initialPage)
  const paginationRange = usePagination({
    totalData,
    dataLimit,
    currentPage,
    siblingCount,
  })
  const [jumperInput, setJumperInput] = useState<string | number>('')

  // Derived States
  const lastPage = paginationRange[paginationRange.length - 1]

  function getValidValue() {
    return !jumperInput || Number.isNaN(jumperInput)
      ? undefined
      : Number(jumperInput)
  }

  // Handler
  function goToNextPage() {
    setCurrentPage((page) => {
      onChange(page + 1)
      setJumperInput(page + 1)
      return page + 1
    })
  }

  function goToPreviousPage() {
    setCurrentPage((page) => {
      onChange(page - 1)
      setJumperInput(page - 1)
      return page - 1
    })
  }

  function goToPage(page) {
    if (!numberRegex.test(page)) {
      return
    }
    setCurrentPage(() => {
      onChange(page)
      setJumperInput(page)
      return page
    })
  }

  function changePage(event) {
    const pageNumber = Number(event.target.textContent)
    setCurrentPage(pageNumber)
    setJumperInput(pageNumber)
    onChange(pageNumber)
  }

  function changeDataLimitDropdown(value: number) {
    setDataLimit(value)
    onChangeDropdown(value)
  }

  const jumperKeyUp = useCallback(
    (e) => {
      if (jumperInput === '' || getValidValue() > lastPage) {
        return
      }
      if (
        getValidValue() &&
        (e.keyCode === ENTER_CODE || e.type === 'click')
      ) {
        goToPage(getValidValue())
      }
    },
    [jumperInput],
  )

  function jumperChange(e) {
    const parsed = e.target.value.replace(/\D/g, '')
    const newValue = parseFloat(parsed)
    setJumperInput(newValue ? Math.max(1, newValue) : '')
  }

  const jumperOnBlur = useCallback(() => {
    if (jumperInput === '') {
      setJumperInput(currentPage)
      return
    }
    if (getValidValue() < lastPage) {
      goToPage(getValidValue())
    }
  }, [jumperInput, lastPage])

  const renderRange = useMemo(() => {
    return (
      <>
        {paginationRange.map((item) => {
          if (typeof item === 'string' && item.includes('DOTS'))
            return <Ellipsis key={item}>...</Ellipsis>

          return (
            <Item
              data-testid={`pagination-item-${item}-test`}
              key={item}
              isActive={currentPage === item}
              color={color}
              onClick={changePage}
            >
              <Typography
                type="bh6"
                color={currentPage === item ? 'white' : 'black'}
              >
                {item}
              </Typography>
            </Item>
          )
        })}
      </>
    )
  }, [color, currentPage, paginationRange])

  useEffect(() => {
    setCurrentPage(initialPage)
    setJumperInput(initialPage)
  }, [dataLimit, initialPage, totalData])

  return (
    <Container data-testid="pagination-test" padding={padding}>
      <Flex>
        <PreviousPageBtn
          data-testid="previous-page-btn-test"
          isDisabled={currentPage === 1}
          color={color}
          onClick={goToPreviousPage}
        >
          <ArrowIcon />
        </PreviousPageBtn>

        {renderRange}

        <NextPageBtn
          data-testid="next-page-btn-test"
          isDisabled={currentPage === lastPage}
          color={color}
          onClick={goToNextPage}
        >
          <ArrowIcon />
        </NextPageBtn>
      </Flex>
      {showDropdown && (
        <Dropdown
          color={color}
          value={dataLimit}
          borderColor={color}
          onChange={changeDataLimitDropdown}
          fontWeight="bold"
          margin="0 9px 0"
        >
          {dropdownValues.map((item) => (
            <option key={item} value={item}>
              {`${item} / Halaman`}
            </option>
          ))}
        </Dropdown>
      )}

      {showJumper && (
        <Flex
          gap="5px"
          alignItems="center"
          data-testid="component-jumper"
        >
          <Typography type="rsubtitle">Pergi ke Halaman</Typography>
          <JumperInput
            value={jumperInput}
            onKeyUp={jumperKeyUp}
            onBlur={jumperOnBlur}
            onChange={jumperChange}
          />
        </Flex>
      )}

      <HiddenPagination
        currentPage={currentPage}
        lastPage={lastPage}
        paginationRange={paginationRange}
        renderHiddenPaginationLink={renderHiddenPaginationLink}
        hiddenPaginationLinkPath={hiddenPaginationLinkPath}
      />
    </Container>
  )
}

export default Pagination
